<template>
    <rkm-modal
        extra-large
        ref="RkmModal"
        title="Problema/condição avaliado neste atendimento"
    >
        <rkm-form
            ref="RkmForm"
            show-legend
        >
            <rkm-row>
                <rkm-col cols="12">
                    <rkm-form-select
                        :disabled="disabledFields.problemaCondicaoPaciente"
                        :options="form.problemasCondicoesPaciente"
                        @change="handleProblemaCondicaoPacienteChange"
                        label="Problemas/condições ativos ou latentes do paciente"
                        show-default-option
                        text-field="descricao"
                        v-model="model.problemaCondicaoAnteriorId"
                        value-field="id"
                    />
                </rkm-col>
            </rkm-row>

            <rkm-row>
                <rkm-col cols="6">
                    <rkm-form-input-autocomplete
                        :disabled="disabledFields.ciap"
                        :repository="repositories.geral.ciap"
                        :request-query-string-params="{
                            cid_id: model.cid ? model.cid.id : null,
                            sexo_id: data.additionals.paciente.sexoId,
                            show_all: true,
                            not_in: [
                                enums.ciap.MEDICINA_FISICA_REABILITACAO,
                                enums.ciap.TUBERCULOSE,
                                enums.ciap.DENGUE_E_OUTRAS_DOENCAS_VIRAIS_NE,
                                enums.ciap.HANSENIASE_E_OUTRAS_DOENCAS_INFECCIOSAS_NE,
                                enums.ciap.FATOR_DE_RISCO_PARA_DOENCA_CARDIOVASCULAR,
                                enums.ciap.HIPERTENSAO_SEM_COMPLICACOES,
                                enums.ciap.ABUSO_AGUDO_DE_ALCOOL,
                                enums.ciap.ABUSO_DO_TABACO,
                                enums.ciap.ABUSO_DE_DROGAS,
                                enums.ciap.DOENCA_PULMONAR_OBSTRUTIVA_CRONICA,
                                enums.ciap.ASMA,
                                enums.ciap.OBESIDADE,
                                enums.ciap.DIABETES_NAO_INSULINO_DEPENDENTE,
                                enums.ciap.DEFICIENCIA_VITAMINICA_NUTRICIONAL,
                                enums.ciap.SINAIS_SINTOMAS_POS_PARTO,
                                enums.ciap.MEDICINA_PREVENTIVA_MANUTENCAO_DA_SAUDE,
                                enums.ciap.CONSULTA_COM_PROFISSIONAL_DE_APS,
                                enums.ciap.OUTROS_PROCEDIMENTOS_PREVENTIVOS
                            ],
                        }"
                        :results-limit= "20"
                        :search-fields="fields.ciap"
                        :text-field="(item) => `${item.codigo} - ${item.descricao}`"
                        :validations="validations.ciap"
                        @select="handleCiapSelect"
                        label="CIAP 2"
                        v-model="model.ciap"
                    />
                </rkm-col>

                <rkm-col cols="6">
                    <rkm-form-input-autocomplete
                        :disabled="disabledFields.cid"
                        :repository="repositories.cid"
                        :request-query-string-params="{
                            ciap_id: model.ciap ? model.ciap.id : null,
                            sexo: data.additionals.paciente.sexoIdentificador,
                        }"
                        :results-limit= "20"
                        :search-fields="fields.cid"
                        :text-field="(item) => `${item.codigo} - ${item.nome}`"
                        :validations="validations.cid"
                        label="CID 10"
                        v-model="model.cid"
                        @select="handleCidSelect"
                    />
                </rkm-col>
            </rkm-row>

            <rkm-row>
                <rkm-col cols="auto">
                    <rkm-form-checkbox
                        :disabled="disabledFields.incluirListaPaciente"
                        @change="handleIncluirListaPacienteChange"
                        v-model="model.incluirListaPaciente"
                    >
                        Incluir na lista de problemas/condições do paciente
                    </rkm-form-checkbox>
                </rkm-col>
            </rkm-row>

            <template v-if="model.incluirListaPaciente">
                <rkm-row class="mt-2">
                    <rkm-col cols="12">
                        <rkm-form-radio-group
                            :disabled="disabledFields.situacao"
                            :options="form.situacoes"
                            :validations="validations.situacao"
                            @change="handleSituacaoChange"
                            label="Situação"
                            text-field="nome"
                            v-model="model.situacaoId"
                            value-field="id"
                        />
                    </rkm-col>
                </rkm-row>

                <rkm-row>
                    <rkm-col :cols="model.situacaoId === enums.problemaCondicaoSituacao.ATIVO ? 12 : 6">
                        <rkm-area
                            title="Início"
                            type="fieldset"
                        >
                            <rkm-row>
                                <rkm-col cols="5">
                                    <rkm-form-date-picker
                                        :max-date="others.moment(model.fimData).format('DD/MM/YYYY') || data.additionals.dataAtual"
                                        :min-date="data.additionals.paciente.dataNascimento"
                                        :validations="validations.inicioData"
                                        @input="handleInicioDataInput"
                                        label="Data"
                                        v-model="model.inicioData"
                                    />
                                </rkm-col>

                                <rkm-col>
                                    <rkm-form-input
                                        :validations="validations.inicioAno"
                                        @change="handleInicioAnoChange"
                                        label="Ano(s)"
                                        number
                                        v-model="model.inicioAno"
                                    />
                                </rkm-col>

                                <rkm-col>
                                    <rkm-form-input
                                        :validations="validations.inicioMes"
                                        @change="handleInicioMesChange"
                                        label="Mês(es)"
                                        number
                                        v-model="model.inicioMes"
                                    />
                                </rkm-col>
                            </rkm-row>
                        </rkm-area>
                    </rkm-col>

                    <rkm-col
                        v-if="model.situacaoId !== enums.problemaCondicaoSituacao.ATIVO"
                        cols="6"
                    >
                        <rkm-area
                            title="Fim"
                            type="fieldset"
                        >
                            <rkm-row>
                                <rkm-col cols="5">
                                    <rkm-form-date-picker
                                        :max-date="data.additionals.dataAtual"
                                        :min-date="others.moment(model.inicioData).format('DD/MM/YYYY') || data.additionals.paciente.dataNascimento"
                                        :validations="validations.fimData"
                                        @input="handleFimDataInput"
                                        label="Fim"
                                        v-model="model.fimData"
                                    />
                                </rkm-col>

                                <rkm-col>
                                    <rkm-form-input
                                        :validations="validations.fimAno"
                                        @change="handleFimAnoChange"
                                        label="Ano(s)"
                                        number
                                        v-model="model.fimAno"
                                    />
                                </rkm-col>

                                <rkm-col>
                                    <rkm-form-input
                                        :validations="validations.fimMes"
                                        @change="handleFimMesChange"
                                        label="Mês(es)"
                                        number
                                        v-model="model.fimMes"
                                    />
                                </rkm-col>
                            </rkm-row>
                        </rkm-area>
                    </rkm-col>
                </rkm-row>

                <rkm-row>
                    <rkm-col cols="12">
                        <rkm-form-textarea
                            :validations="validations.observacao"
                            label="Observações"
                            rows="4"
                            v-model="model.observacao"
                        />
                    </rkm-col>
                </rkm-row>
            </template>
        </rkm-form>

        <template v-slot:footer="{cancel}">
            <rkm-button @click="cancel">
                Fechar
            </rkm-button>

            <rkm-button
                @click="save"
                color="primary"
            >
                {{item ? 'Salvar' : 'Incluir'}}
            </rkm-button>
        </template>
    </rkm-modal>
</template>

<script>
// Enums.
import ciap from '@/enums/geral/ciap'
import grupoCiap from '@/enums/geral/grupoCiap'
import problemaCondicaoSituacao from '@/enums/ambulatorio/problemaCondicaoSituacao'

// Mixins.
import {atencaoBasicaAtendimentoIndividualPreNatal, atencaoBasicaAtendimentoIndividualProblemaCondicao, error, object, repository} from '@/mixins'

// Others.
import moment from 'moment'

// Vuex.
import {mapActions} from 'vuex'

export default {
    computed: {
        form() {
            return {
                problemasCondicoesPaciente: this.getProblemasCondicoesPaciente().map(problemaCondicaoPaciente => {
                    return problemaCondicaoPaciente.ciap.id
                        ? {id: problemaCondicaoPaciente.id, descricao: `${problemaCondicaoPaciente.ciap.codigo} - ${problemaCondicaoPaciente.ciap.descricao}`}
                        : {id: problemaCondicaoPaciente.id, descricao: `${problemaCondicaoPaciente.cid.codigo} - ${problemaCondicaoPaciente.cid.nome}`}
                }),
                situacoes: this.data.form.problemaCondicaoSituacoes.map(situacao => {
                    let disabled = false

                    if (situacao.id === this.enums.problemaCondicaoSituacao.LATENTE) {
                        disabled = this.hasGravidezNormalAdicionadaAnteriormente && this.isGravidezNormal(this.model)
                    }

                    return {
                        id: situacao.id,
                        nome: situacao.nome,
                        disabled,
                    }
                }),
            }
        },
        disabledFields() {
            const model = this.model
            const hasItem = !!this.item
            const hasProblemaCondicaoPaciente = !!model.problemaCondicaoAnteriorId
            const hasGravidezNormalAdicionadaAnteriormente = this.hasGravidezNormalAdicionadaAnteriormente
            const isGravidezNormal = this.isGravidezNormal(model)
            const isGravidezAltoRisco = this.isGravidezAltoRisco(model)

            return {
                problemaCondicaoPaciente: hasItem,
                ciap: hasProblemaCondicaoPaciente || hasItem,
                cid: hasProblemaCondicaoPaciente || hasItem || isGravidezNormal,
                incluirListaPaciente: hasProblemaCondicaoPaciente || isGravidezNormal || isGravidezAltoRisco || !!this.model.id,
                situacao: (!hasGravidezNormalAdicionadaAnteriormente && isGravidezNormal) || (hasItem && isGravidezAltoRisco),
            }
        },
        validations() {
            return {
                ciap: {required: !this.model.cid},
                cid: {required: !this.model.ciap},
                situacao: {required: this.model.incluirListaPaciente},
                inicioData: {required: true},
                inicioAno: {max: 3},
                inicioMes: {
                    max: 2,
                    max_value: 11,
                },
                fimData: {required: true},
                fimAno: {max: 3},
                fimMes: {
                    max: 2,
                    max_value: 11,
                },
                observacao: {max: 500},
            }
        },
        hasGravidezNormalAdicionadaAnteriormente() {
            return !!this.getProblemaCondicaoPacienteGravidezNormal()
        },
        hasGravidezNormal() {
            return !!this.getProblemaCondicaoGravidezNormal()
        },
        hasDesfechoGravidez() {
            return this.getProblemasCondicoesDesfechoGravidez().length > 0
        },
    },
    created() {
        this.repositories = {
            cid: this.getConectaSusRepository('sigtap.cid'),
            geral: this.getConectaSusRepository('geral'),
        }
    },
    data: () => ({
        enums: {ciap, problemaCondicaoSituacao},
        fields: {
            ciap: [
                {key: 'geral_ciap.codigo', searchable: true},
                {key: 'geral_ciap.descricao', searchable: true},
            ],
            cid: [
                {key: 'codigo', searchable: true},
                {key: 'nome', searchable: true},
            ],
        },
        item: null,
        model: {
            id: null,
            automatico: false,
            problemaCondicaoAnteriorId: null,
            ciap: null,
            cid: null,
            incluirListaPaciente: false,
            situacaoId: null,
            inicioData: null,
            inicioAno: null,
            inicioMes: null,
            fimData: null,
            fimAno: null,
            fimMes: null,
            observacao: null,
        },
        others: {moment},
    }),
    methods: {
        ...mapActions(['setLoading', 'unsetLoading']),
        open(item = null) {
            this.item = item

            if (this.item) {
                this.edit()
            } else {
                this.create()
            }

            this.$refs.RkmModal.show()
        },
        getProblemaCondicaoPacienteGravidezNormal() {
            return this.data.form.problemasCondicoesPaciente.find(problemaCondicaoPaciente => {
                let isCiapGravidezNormal = false
                let isCidGravidezNormal = false

                if (problemaCondicaoPaciente.ciap) {
                    isCiapGravidezNormal = this.ciapsGravidez.normal.includes(problemaCondicaoPaciente.ciap.codigo)
                }

                if (problemaCondicaoPaciente.cid) {
                    isCidGravidezNormal = this.cidsGravidez.normal.includes(problemaCondicaoPaciente.cid.codigo)
                }

                return isCiapGravidezNormal || isCidGravidezNormal
            })
        },
        getProblemaCondicaoGravidezNormal() {
            return this.data.model.atendimento.problemasCondicoes.find(this.isGravidezNormal)
        },
        getProblemasCondicoesDesfechoGravidez() {
            return this.data.model.atendimento.problemasCondicoes.filter(this.isDesfechoGravidez)
        },
        create() {
            this.resetForm()
        },
        edit() {
            this.model = this.clone({
                id: this.item.id,
                automatico: this.item.automatico,
                problemaCondicaoAnteriorId: this.item.problemaCondicaoAnteriorId,
                ciap: this.item.ciap,
                cid: this.item.cid,
                incluirListaPaciente: this.item.incluirListaPaciente,
                situacaoId: this.item.situacaoId,
                inicioData: this.item.inicio ? this.others.moment(this.item.inicio.data, 'DD/MM/YYYY').format('YYYY-MM-DD') : null,
                inicioMes: this.item.inicio ? this.item.inicio.mes : null,
                inicioAno: this.item.inicio ? this.item.inicio.ano : null,
                fimData: this.item.fim ? this.others.moment(this.item.fim.data, 'DD/MM/YYYY').format('YYYY-MM-DD') : null,
                fimMes: this.item.fim ? this.item.fim.mes : null,
                fimAno: this.item.fim ? this.item.fim.ano : null,
                observacao: this.item.observacao,
            })

            if (this.model.inicioData) {
                this.handleInicioDataInput(this.model.inicioData)
            }

            if (this.model.fimData) {
                this.handleFimDataInput(this.model.fimData)
            }
        },
        store(model) {
            const preparedModel = this.prepareData(model)

            this.data.model.atendimento.problemasCondicoes.push(preparedModel)

            return preparedModel
        },
        update(model) {
            Object.assign(this.item, this.prepareData(model))
        },
        save() {
            this.$refs.RkmForm.validate()
                .then(valid => {
                    if (valid === false) return

                    if (!this.item) {
                        const hasProblemaCondicaoCiap = this.model.ciap ? this.hasProblemaCondicaoByCiapId(this.model.ciap.id) : this.hasProblemaCondicaoWhereCiapIsNull()
                        const hasProblemaCondicaoCid = this.model.cid ? this.hasProblemaCondicaoByCidId(this.model.cid.id) : this.hasProblemaCondicaoWhereCidIsNull()

                        if (hasProblemaCondicaoCiap && hasProblemaCondicaoCid) {
                            this.showValidationNotification('O problema/condição já foi incluído.')
                            return
                        }
                    }

                    if(this.model.ciap != null && (this.model.ciap.grupoId === grupoCiap.PROCEDIMENTOS)){
                        this.showValidationNotification('Ciap do grupo de procedimentos não pode ser utilizado aqui.')
                        return
                    }

                    if (this.hasGravidezNormal && this.isDesfechoGravidez(this.model)) {
                        this.showValidationNotification('Para registrar desfecho de gravidez remova o CID e/ou CIAP de gravidez.')
                        return
                    }

                    if (this.hasDesfechoGravidez && this.isGravidezNormal(this.model)) {
                        this.showValidationNotification('Para registrar CID e/ou CIAP de gravidez remova a condição de desfecho de gravidez.')
                        return
                    }

                    if (!this.hasGravidezNormal && !this.hasGravidezNormalAdicionadaAnteriormente && this.isGravidezAltoRisco(this.model)) {
                        if (this.model.situacaoId === this.enums.problemaCondicaoSituacao.ATIVO) {
                            this.storeGravidezNormal()
                        }
                    }

                    if (this.item) {
                        this.update(this.model)
                    } else {
                        this.store(this.model)
                    }

                    this.$refs.RkmModal.hide()
                })
        },
        storeGravidezNormal() {
            Promise.all([
                this.getCiapByCodigo(this.enums.ciap.GRAVIDEZ),
                this.getCidByCodigo(this.enums.cid.SUPERVISAO_DE_GRAVIDEZ_NORMAL),
            ]).then(([responseCiap, responseCid]) => {
                const ciap = responseCiap.data[0]
                const cid = responseCid.data[0]

                let problemaCondicaoGravidez = {
                    automatico: true,
                    ciap: {
                        id: ciap.id,
                        codigo: ciap.codigo,
                        descricao: ciap.descricao,
                    },
                    cid: {
                        id: cid.id,
                        codigo: cid.codigo,
                        nome: cid.nome,
                    },
                }

                this.store(Object.assign(this.model, problemaCondicaoGravidez, this.getPreparedModelGravidezNormalAutomatica()))
            })
        },
        prepareData(model) {
            const situacao = this.data.form.problemaCondicaoSituacoes.find(situacao => situacao.id === model.situacaoId)

            return {
                automatico: model.automatico,
                problemaCondicaoAnteriorId: model.problemaCondicaoAnteriorId,
                id: model.id,
                ciap: model.ciap ? {
                    id: model.ciap.id,
                    codigo: model.ciap.codigo,
                    descricao: model.ciap.descricao,
                } : null,
                cid: model.cid ? {
                    id: model.cid.id,
                    codigo: model.cid.codigo,
                    nome: model.cid.nome,
                } : null,
                incluirListaPaciente: model.incluirListaPaciente,
                situacaoId: situacao ? situacao.id : null,
                situacaoNome: situacao ? situacao.nome : null,
                inicio: model.inicioData ? {
                    data: this.others.moment(model.inicioData).format('DD/MM/YYYY'),
                    ano: model.inicioAno,
                    mes: model.inicioMes,
                } : null,
                fim: model.fimData ? {
                    data: this.others.moment(model.fimData).format('DD/MM/YYYY'),
                    ano: model.fimAno,
                    mes: model.fimMes,
                } : null,
                observacao: model.observacao,
            }
        },
        resetForm() {
            this.item = null
            this.model.id = null
            this.model.automatico = false
            this.model.problemaCondicaoAnteriorId = null
            this.model.ciap = null
            this.model.cid = null
            this.model.cidId = null
            this.model.incluirListaPaciente = false
            this.model.situacaoId = null
            this.model.inicioData = null
            this.model.inicioAno = null
            this.model.inicioMes = null
            this.model.fimData = null
            this.model.fimAno = null
            this.model.fimMes = null
            this.model.observacao = null
        },
        handleProblemaCondicaoPacienteChange(value) {
            this.model.ciap = null
            this.model.cid = null
            this.model.incluirListaPaciente = false
            this.model.situacaoId = null
            this.model.inicioData = null
            this.model.inicioAno = null
            this.model.inicioMes = null
            this.model.fimData = null
            this.model.fimAno = null
            this.model.fimMes = null
            this.model.observacao = null

            const problemaCondicaoPaciente = this.getProblemaCondicaoPaciente(value)

            if (problemaCondicaoPaciente) {
                Object.assign(this.model, this.getPreparedProblemaCondicaoPaciente(problemaCondicaoPaciente))
            }
        },
        handleCiapSelect(value) {
            const problemaCondicaoPaciente = this.getProblemaCondicaoPacienteByCiapId(value.id)

            if (problemaCondicaoPaciente) {
                Object.assign(this.model, this.getPreparedProblemaCondicaoPaciente(problemaCondicaoPaciente))
            }
            else if (this.isGravidezNormal(this.model)) {
                this.getCidByCodigo(this.enums.cid.SUPERVISAO_DE_GRAVIDEZ_NORMAL).then(response => Object.assign(this.model, this.getPreparedModelGravidezNormalAutomaticaByCid(response.data[0])))
            }
            else if (this.isGravidezAltoRisco(this.model) || this.isDesfechoGravidez(this.model)) {
                Object.assign(this.model, this.getPreparedModelGravidezAutomatica())
            }

            if (value.cidId) {
                this.model.cid = {
                    id: value.cidId,
                    codigo: value.cidCodigo,
                    nome: value.cidNome,
                }
            }
        },
        handleCidSelect(value) {
            const problemaCondicaoPaciente = this.getProblemaCondicaoPacienteByCidId(value.id)

            if (problemaCondicaoPaciente) {
                Object.assign(this.model, this.getPreparedProblemaCondicaoPaciente(problemaCondicaoPaciente))
            }
            else if (this.isGravidezNormal(this.model)) {
                this.getCiapByCodigo(this.enums.ciap.GRAVIDEZ).then(response => Object.assign(this.model, this.getPreparedModelGravidezNormalAutomaticaByCiap(response.data[0])))
            }
            else if (this.isGravidezAltoRisco(this.model) || this.isDesfechoGravidez(this.model)) {
                Object.assign(this.model, this.getPreparedModelGravidezAutomatica())
            }
        },
        handleIncluirListaPacienteChange(value) {
            this.model.situacaoId = value ? this.enums.problemaCondicaoSituacao.ATIVO : null,
            this.model.inicioData = null
            this.model.inicioAno = null
            this.model.inicioMes = null
            this.model.fimData = null
            this.model.fimAno = null
            this.model.fimMes = null
            this.model.observacao = null
        },
        handleSituacaoChange() {
            if (this.model.situacaoId === this.enums.problemaCondicaoSituacao.ATIVO) {
                this.model.fimData = null
                this.model.fimAno = null
                this.model.fimMes = null
            }
        },
        handleInicioDataInput(value) {
            const dataSelecionada = this.others.moment(value)
            const dataNascimento = this.others.moment(this.data.additionals.paciente.dataNascimento, 'DD/MM/YYYY')

            const anos = dataSelecionada.diff(dataNascimento, 'years')
            const meses = dataSelecionada.diff(dataNascimento, 'months')

            this.model.inicioAno = anos
            this.model.inicioMes = meses % 12
        },
        handleInicioAnoChange(value) {
            this.model.inicioData = this.others.moment(this.data.additionals.paciente.dataNascimento, 'DD/MM/YYYY')
                .add(value, 'years')
                .add(this.model.inicioMes, 'months')
                .format('YYYY-MM-DD')
        },
        handleInicioMesChange(value) {
            this.model.inicioData = this.others.moment(this.data.additionals.paciente.dataNascimento, 'DD/MM/YYYY')
                .add(this.model.inicioAno, 'years')
                .add(value, 'months')
                .format('YYYY-MM-DD')
        },
        handleFimDataInput(value) {
            const dataSelecionada = this.others.moment(value)
            const dataNascimento = this.others.moment(this.data.additionals.paciente.dataNascimento, 'DD/MM/YYYY')

            const anos = dataSelecionada.diff(dataNascimento, 'years')
            const meses = dataSelecionada.diff(dataNascimento, 'months')

            this.model.fimAno = anos
            this.model.fimMes = meses % 12
        },
        handleFimAnoChange(value) {
            this.model.fimData = this.others.moment(this.data.additionals.paciente.dataNascimento, 'DD/MM/YYYY')
                .add(value, 'years')
                .add(this.model.fimMes, 'months')
                .format('YYYY-MM-DD')
        },
        handleFimMesChange(value) {
            this.model.fimData = this.others.moment(this.data.additionals.paciente.dataNascimento, 'DD/MM/YYYY')
                .add(this.model.fimAno, 'years')
                .add(value, 'months')
                .format('YYYY-MM-DD')
        },
        async getCiapByCodigo(codigo) {
            this.setLoading()

            try {
                return await this.repositories.geral.ciap.index({codigo})
            }
            catch (error) {
                this.handleError(error)
            }
            finally {
                this.unsetLoading()
            }
        },
        async getCidByCodigo(codigo) {
            this.setLoading()

            try {
                return await this.repositories.cid.index({codigo})
            }
            catch (error) {
                this.handleError(error)
            }
            finally {
                this.unsetLoading()
            }
        },
        hasProblemaCondicaoByCiapId(id) {
            return this.getProblemasCondicoes().some(problemaCondicaoPaciente => {
                if (problemaCondicaoPaciente.ciap) {
                    return problemaCondicaoPaciente.ciap.id === id
                }
            })
        },
        hasProblemaCondicaoWhereCiapIsNull() {
            return this.getProblemasCondicoes().some(problemaCondicaoPaciente => !problemaCondicaoPaciente.ciap)
        },
        hasProblemaCondicaoByCidId(id) {
            return this.getProblemasCondicoes().some(problemaCondicao => {
                if (problemaCondicao.cid) {
                    return problemaCondicao.cid.id === id
                }
            })
        },
        hasProblemaCondicaoWhereCidIsNull() {
            return this.getProblemasCondicoes().some(problemaCondicaoPaciente => !problemaCondicaoPaciente.cid)
        },
        getProblemasCondicoes() {
            return this.data.model.atendimento.problemasCondicoes
        },
        getProblemasCondicoesPaciente() {
            return this.data.form.problemasCondicoesPaciente
        },
        getProblemaCondicaoPaciente(id) {
            return this.getProblemasCondicoesPaciente().find(problemaCondicaoPaciente => problemaCondicaoPaciente.id === id)
        },
        getProblemaCondicaoPacienteByCiapId(id) {
            return this.getProblemasCondicoesPaciente().find(problemaCondicaoPaciente => {
                if (problemaCondicaoPaciente.ciap) {
                    return problemaCondicaoPaciente.ciap.id === id
                }
            })
        },
        getProblemaCondicaoPacienteByCidId(id) {
            return this.getProblemasCondicoesPaciente().find(problemaCondicaoPaciente => {
                if (problemaCondicaoPaciente.cid) {
                    return problemaCondicaoPaciente.cid.id === id
                }
            })
        },
        getPreparedModelGravidezAutomatica() {
            const dataSelecionada = this.others.moment(this.data.additionals.dataAtual, 'DD/MM/YYYY')
            const dataNascimento = this.others.moment(this.data.additionals.paciente.dataNascimento, 'DD/MM/YYYY')

            const anos = dataSelecionada.diff(dataNascimento, 'years')
            const meses = dataSelecionada.diff(dataNascimento, 'months')

            return {
                incluirListaPaciente: true,
                inicioData: dataSelecionada.toDate(),
                inicioAno: anos,
                inicioMes: meses % 12,
            }
        },
        getPreparedModelGravidezNormalAutomatica() {
            return {
                ...this.getPreparedModelGravidezAutomatica(),
                situacaoId: this.enums.problemaCondicaoSituacao.ATIVO,
            }
        },
        getPreparedModelGravidezNormalAutomaticaByCiap(ciap) {
            return {
                ...this.getPreparedModelGravidezNormalAutomatica(),
                ciap: {
                    id: ciap.id,
                    codigo: ciap.codigo,
                    descricao: ciap.descricao,
                }
            }
        },
        getPreparedModelGravidezNormalAutomaticaByCid(cid) {
            return {
                ...this.getPreparedModelGravidezNormalAutomatica(),
                cid: {
                    id: cid.id,
                    codigo: cid.codigo,
                    nome: cid.nome,
                }
            }
        },
    },
    mixins: [atencaoBasicaAtendimentoIndividualPreNatal, atencaoBasicaAtendimentoIndividualProblemaCondicao, error, object, repository],
    props: {
        data: {required: true, type: Object},
    },
    watch: {
        'model.ciap'(value) {
            if (!value) {
                this.model.cid = null
                this.model.cidId = null
                this.model.incluirListaPaciente = false
                this.model.situacaoId = null
                this.model.inicioData = null
                this.model.inicioAno = null
                this.model.inicioMes = null
                this.model.fimData = null
                this.model.fimAno = null
                this.model.fimMes = null
                this.model.observacao = null
            }
        },
    },
}
</script>